import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, XCircle, AlertTriangle,
  TrendingUp, BarChart2, Brain, Download
} from 'lucide-react';
import { api } from '../api/client';

const VERDICT_STYLE = {
  PASS:    { color: 'text-green-400',  bg: 'bg-green-400/10',  border: 'border-green-400/20',  icon: CheckCircle   },
  FAIL:    { color: 'text-red-400',    bg: 'bg-red-400/10',    border: 'border-red-400/20',    icon: XCircle       },
  REVIEW:  { color: 'text-amber-400',  bg: 'bg-amber-400/10',  border: 'border-amber-400/20',  icon: AlertTriangle },
  INVALID: { color: 'text-slate-500',  bg: 'bg-slate-500/10',  border: 'border-slate-500/20',  icon: XCircle       },
  PENDING: { color: 'text-slate-500',  bg: 'bg-slate-500/10',  border: 'border-slate-500/20',  icon: AlertTriangle },
};

function MetricCard({ label, value, sub }) {
  return (
    <div className="bg-[#0A0A0F] border border-[#1E1E2E] rounded p-4">
      <p className="text-[10px] font-mono text-slate-600 uppercase tracking-wider mb-1">{label}</p>
      <p className="font-mono font-bold text-slate-100 text-lg">{value}</p>
      {sub && <p className="text-[10px] font-mono text-slate-600 mt-0.5">{sub}</p>}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Mexico_City'
  });
}

export default function GymBacktestDetail() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    api.getBacktestDetail(id)
      .then(r => setData(r.data))
      .catch(() => setError('No se pudo cargar el detalle'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="flex items-center gap-3 text-amber-500 font-mono text-sm">
        <div className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
        Cargando detalle...
      </div>
    </div>
  );

  if (error || !data) return (
    <div className="p-8">
      <div className="flex items-center gap-2 text-red-400 text-xs font-mono border border-red-400/20 bg-red-400/5 px-3 py-2 rounded">
        <AlertTriangle size={12} /> {error || 'Resultado no encontrado'}
      </div>
    </div>
  );

  const verdict = data.claudeAnalysis?.verdict || 'PENDING';
  const vs      = VERDICT_STYLE[verdict] || VERDICT_STYLE.PENDING;
  const VIcon   = vs.icon;
  const m       = data.technicalMetrics;
  const cl      = data.claudeAnalysis;

  return (
    <div className="p-8 space-y-8 max-w-5xl">

      {/* Back */}
      <button
        onClick={() => navigate('/gym/backtests')}
        className="flex items-center gap-2 text-slate-500 hover:text-slate-300 font-mono text-[11px] transition-colors"
      >
        <ArrowLeft size={14} /> Volver a backtests
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-display text-2xl font-bold text-slate-100 tracking-tight">
              {data.symbol} · {data.timeframe}
            </h1>
            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded border text-[11px] font-mono ${vs.color} ${vs.bg} ${vs.border}`}>
              <VIcon size={11} /> {verdict}
            </span>
          </div>
          <p className="text-xs text-slate-500 font-mono">
            {data.strategy} · {formatDate(data.periodStart)} → {formatDate(data.periodEnd)}
          </p>
        </div>
        <button
          onClick={() => window.open(`https://claudio.trading/api/gym/results/${id}/export`, '_blank')}
          className="flex items-center gap-2 px-3 py-2 rounded border border-[#2A2A3E] text-slate-500 hover:text-amber-400 hover:border-amber-400/30 transition-colors text-[11px] font-mono"
        >
          <Download size={12} /> Exportar CSV
        </button>
      </div>

      {/* Métricas técnicas */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <BarChart2 size={14} className="text-amber-400" />
          <h2 className="font-mono font-bold text-slate-300 text-sm">Métricas</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard label="Total trades"   value={m.totalTrades} />
          <MetricCard label="Win Rate"       value={m.winRate ? `${m.winRate.toFixed(1)}%` : '—'} sub={`${m.winningTrades}W · ${m.losingTrades}L`} />
          <MetricCard label="Profit Factor"  value={m.profitFactor ? m.profitFactor.toFixed(2) : '—'} />
          <MetricCard label="Avg R:R"        value={m.avgRR ? m.avgRR.toFixed(2) : '—'} />
          <MetricCard label="PnL Pips"       value={m.totalPnlPips ? m.totalPnlPips.toFixed(1) : '—'} />
          <MetricCard label="Max Drawdown"   value={m.maxDrawdown ? `${m.maxDrawdown.toFixed(1)}%` : '—'} />
          <MetricCard label="Ready for Live" value={cl.readyForLive ? 'SÍ' : 'NO'} />
          <MetricCard label="Confidence"     value={cl.confidence ? `${(cl.confidence * 100).toFixed(0)}%` : '—'} />
        </div>
      </div>

      {/* Análisis Claude */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Brain size={14} className="text-amber-400" />
          <h2 className="font-mono font-bold text-slate-300 text-sm">Análisis Claude</h2>
        </div>
        <div className="space-y-4">

          {cl.technicalAnalysis && (
            <div className="bg-[#13131A] border border-[#1E1E2E] rounded-lg p-5">
              <p className="text-[10px] font-mono text-slate-600 uppercase tracking-wider mb-3">Análisis Técnico</p>
              <p className="text-[12px] font-mono text-slate-400 leading-relaxed">{cl.technicalAnalysis}</p>
            </div>
          )}

          {cl.fundamentalImpact && (
            <div className="bg-[#13131A] border border-[#1E1E2E] rounded-lg p-5">
              <p className="text-[10px] font-mono text-slate-600 uppercase tracking-wider mb-3">Impacto Fundamental</p>
              <p className="text-[12px] font-mono text-slate-400 leading-relaxed">{cl.fundamentalImpact}</p>
            </div>
          )}

          {cl.suggestions?.length > 0 && (
            <div className="bg-[#13131A] border border-[#1E1E2E] rounded-lg p-5">
              <p className="text-[10px] font-mono text-slate-600 uppercase tracking-wider mb-3">Sugerencias</p>
              <ul className="space-y-2">
                {cl.suggestions.map((s, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-amber-400 font-mono text-[11px] shrink-0 mt-0.5">{String(i+1).padStart(2,'0')}.</span>
                    <p className="text-[12px] font-mono text-slate-400 leading-relaxed">{s}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}